"use client";

import { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { clientAPI } from "@/lib/client-fetch";

export type StoreLocation = {
  city: string | null;
  microhubId: string | null;
  addressId: string | number | null;
  fullAddressStr: string | null;
};

type SetStoreLocationInput = {
  city?: string | null;
  microhubId?: string | null;
  addressId?: string | number | null;
  fullAddressStr?: string | null;
};

type StoreLocationContextType = {
  storeLocation: StoreLocation;
  setStoreLocation: (input: SetStoreLocationInput) => Promise<boolean>;
  clearStoreLocation: () => Promise<boolean>;
  hasStoreLocation: boolean;
};

const StoreLocationContext = createContext<StoreLocationContextType | undefined>(undefined);

type StoreLocationProviderProps = {
  children: ReactNode;
  initialStoreLocation?: StoreLocation;
};

const EMPTY: StoreLocation = { city: null, microhubId: null, addressId: null, fullAddressStr: null };

export function StoreLocationProvider({ children, initialStoreLocation }: StoreLocationProviderProps) {
  const [storeLocation, setStoreLocationState] = useState<StoreLocation>(
    initialStoreLocation ?? EMPTY
  );

  const setStoreLocation = useCallback(async (input: SetStoreLocationInput): Promise<boolean> => {
    const result = await clientAPI.post("/api/store-location", input);
    if (result.ok) {
      setStoreLocationState((prev) => ({
        city: "city" in input ? (input.city ?? null) : prev.city,
        microhubId: "microhubId" in input ? (input.microhubId ?? null) : prev.microhubId,
        addressId: "addressId" in input ? (input.addressId ?? null) : prev.addressId,
        fullAddressStr: "fullAddressStr" in input ? (input.fullAddressStr ?? null) : prev.fullAddressStr,
      }));
      return true;
    }
    return false;
  }, []);

  const clearStoreLocation = useCallback(async (): Promise<boolean> => {
    const result = await clientAPI.delete("/api/store-location");
    if (result.ok) {
      setStoreLocationState(EMPTY);
      return true;
    }
    return false;
  }, []);

  const hasStoreLocation = storeLocation.city !== null && storeLocation.microhubId !== null;

  return (
    <StoreLocationContext.Provider
      value={{
        storeLocation,
        setStoreLocation,
        clearStoreLocation,
        hasStoreLocation,
      }}
    >
      {children}
    </StoreLocationContext.Provider>
  );
}

export function useStoreLocation() {
  const context = useContext(StoreLocationContext);
  if (context === undefined) {
    throw new Error("useStoreLocation must be used within a StoreLocationProvider");
  }
  return context;
}
